import { formatDisplayValue } from "@common/lib/format";
import { type I18n, localize } from "@common/lib/i18n";
import { evalInContext } from "@common/lib/misc";
import { Common } from "@common/types";
import { type ChartConfiguration, type ChartData, type ChartType as ChartJsType, type ChartOptions, type LinearScaleOptions } from "chart.js";
import { groupBy, maxBy, minBy, set, uniq, values } from "lodash";
import { type BubbleChartPoint } from "./component";
import { type BubbleScatterChartConfig, type ChartAxis, type ChartBaseConfig, type ChartConfig, type ChartType, type OnClickHandler } from "./types";

const COLORS = ["Blue", "Orange", "Green", "Purple", "SoftBlue", "SoftOrange", "SoftGreen", "SoftPurple"];

export const EMPTY_CHART_CONFIG: ChartConfig = {
  aspectRatio: 2.5,
  drillDownKey: "",
  xAxis: { key: "", dataType: "string" },
  yAxis: []
};

export const getChartType = (type: ChartType): ChartJsType => {
  switch (type) {
    case "Area":
      return "line";
    case "Bar":
      return "bar";
    case "Bubble":
      return "bubble";
    case "Doughnut":
      return "doughnut";
    case "HorizontalBar":
      return "bar";
    case "Line":
      return "line";
    case "MultiSeriesPie":
      return "pie";
    case "Pie":
      return "pie";
    case "Polar":
      return "polarArea";
    case "Radar":
      return "radar";
    case "Scatter":
      return "scatter";
    case "SteppedLine":
      return "line";
  }
};

const doesSupportStacking = (type: ChartType): boolean => type === "Area" || type === "Bar";

export const getEmptyConfig = (type: ChartType): ChartConfiguration => {
  return {
    data: {
      datasets: []
    },
    type: getChartType(type)
  };
};

const getColor = (mixed: undefined | string | string[], index: number, colors?: string[]): ChartAxis["color"] => {
  const color = mixed
    ? Array.isArray(mixed)
      ? mixed[Math.min(index, mixed.length - 1)]
      : mixed
    : colors
    ? colors[index]
    : `{$${COLORS[index]}}`;

  const colorMatch = color?.match(/^\{\$([^}]+)\}$/);
  if (colorMatch) {
    // e.g. SoftOrange will become --softOrange
    const name = colorMatch[1].replace(/^([A-Z])/, (_, x) => x.toLowerCase()); // lowercase first letter
    const style = getComputedStyle(document.body);
    return style.getPropertyValue(`--${name}`);
  }

  return color;
};

export const getDisplayValue = (axis: ChartAxis, data: object) => {
  if (axis.data) {
    return evalInContext({ f: data }, axis.data.replace(/f\./g, "this.f."));
  }

  return formatDisplayValue(data[axis.key], axis.dataType);
};

// TODO: might be not complete
const needsArrayColor = (type: ChartType): boolean => type === "Doughnut" || type === "Pie";

export const getData = (i18n: I18n, type: ChartType, config: ChartConfig, data: object[]): ChartData => {
  const labels =
    type === "MultiSeriesPie"
      ? config.yAxis.reduce((res, x) => [...res, ...(Array.isArray(x.label) ? x.label : [x.label])], [])
      : uniq(data.map((x) => getDisplayValue(config.xAxis, x)));

  return {
    datasets: config.yAxis
      .filter((x) => config.yNoData === "show" || data.some((y) => x.key in y))
      .map((x, i) => {
        const color =
          x.isStacked || !needsArrayColor(type)
            ? getColor(x.color, i, config.colors)
            : (data.map((_, i) => getColor(config.xAxis.color ?? x.color, i, config.colors)) as any);

        return {
          indexAxis: type === "HorizontalBar" ? "y" : undefined,
          backgroundColor: color,
          label: x.label
            ? Array.isArray(x.label)
              ? localize(i18n, x.label)
              : [x.label]
            : (data.map((y) => getDisplayValue(x, y)) as any),
          data:
            type === "Scatter"
              ? data.map((y) => ({ x: y[config.xAxis.key], y: y[x.key] }))
              : data.map((y) => getDisplayValue(x, y)),
          stack: x.isStacked && doesSupportStacking(type) ? "Stack" : undefined,
          borderColor: color,
          borderWidth: type === "MultiSeriesPie" || type === "Bar" ? 0 : 3,
          pointStyle: "circle",
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBorderWidth: 0,
          pointBackgroundColor: color,
          pointHoverBackgroundColor: color,
          pointHoverBorderWidth: 0,
          fill: type === "Area" ? true : undefined,
          stepped: type === "SteppedLine" ? true : undefined
        };
      }),
    labels
  };
};

const getEdgeValue = (
  type: ChartType,
  groupByKey: string,
  axis: ChartAxis[],
  data: object[],
  getEdge: (...args: number[]) => number
): number => {
  if (axis.some((x) => x.isStacked) && doesSupportStacking(type)) {
    const stacked = axis.filter((x) => x.isStacked);
    const vs = values(groupBy(data, (x) => x[groupByKey])).map((o) => {
      const item = o[0];
      return stacked.reduce((res, x) => res + (isNaN(parseFloat(item[x.key])) ? 0 : parseFloat(item[x.key])), 0);
    });
    return getEdge(...vs);
  }
  return getEdge(
    ...axis.map((x) => getEdge(...[0, ...data.map((y) => (isNaN(parseFloat(y[x.key])) ? 0 : parseFloat(y[x.key])))]))
  );
};

const getCommonOptions = (config: ChartBaseConfig): ChartOptions => ({
  hover: {
    mode: "point"
  },
  interaction: {
    intersect: false
  },
  plugins: {
    legend: {
      display: config.legend !== "hide",
      position: "bottom"
    },
    tooltip: {
      callbacks: {
        label: config.tooltip?.label ? (x) => config.tooltip!.label!(x.dataset.data[x.dataIndex], x) : undefined,
        title: config.tooltip?.title ? (x) => config.tooltip!.title!(x) : undefined
      },
      displayColors: config.tooltip?.displayColors,
      padding: 8,
      bodyFont: {
        family: "Inter",
        size: 14,
        lineHeight: 1.4
      },
      titleFont: {
        family: "Inter",
        size: 14
      },
      footerFont: {
        family: "Inter",
        size: 14
      }
    }
  },
  maintainAspectRatio: false,
  responsive: true
});
export const getChartOptions = (
  chartjs: any,
  type: ChartType,
  config: ChartConfig,
  data: object[],
  onClick: OnClickHandler
): ChartOptions => {
  const yMin = config.isPercent ? 0 : getEdgeValue(type, config.xAxis.key, config.yAxis, data, Math.min);
  const yMax = config.isPercent ? 1 : getEdgeValue(type, config.xAxis.key, config.yAxis, data, Math.max);

  const style = getComputedStyle(document.body);
  const gridColor = style.getPropertyValue("--separatorColor");

  const commonOptions = getCommonOptions(config);
  const options: ChartOptions = {
    ...commonOptions,
    onHover: function (e, el) {
      const canvas = e.native?.target as HTMLCanvasElement;
      canvas.style.cursor = el[0] && config.drillDownKey ? "pointer" : "default";
    },
    onClick,
    scales: {
      ...commonOptions.scales,
      y: {
        ...commonOptions.scales?.y,
        ticks: {
          ...commonOptions.scales?.y?.ticks,
          format: {
            style: config.isPercent ? "percent" : undefined,
            maximumFractionDigits: config.isPercent ? 2 : undefined
          }
        },
        suggestedMin: yMin < 0 ? yMin / 1.1 : 0,
        suggestedMax: yMax * 1.1,
        stacked: doesSupportStacking(type) && config.yAxis.some((x) => x.isStacked ?? false),
        grid: { color: gridColor }
      } as LinearScaleOptions // TODO: type script
    }
  };

  if (type === "HorizontalBar") options.indexAxis = "y";

  if (type === "Line") {
    options.scales!.x = {
      ...options.scales!.x,
      grid: { color: gridColor }
    };
  }

  if (config.xAxis.stepSize) {
    set(options, "scales.x.ticks.stepSize", config.xAxis.stepSize);
  }

  if (type === "MultiSeriesPie") {
    Object.assign(options.plugins!.legend as any, {
      labels: {
        // copied from https://www.chartjs.org/docs/3.3.0/samples/other-charts/multi-series-pie.html
        generateLabels: function (chart) {
          // Get the default label list
          const original = chartjs.Chart.overrides.pie.plugins.legend.labels.generateLabels;
          const labelsOriginal = original.call(this, chart);

          // Build an array of colors used in the datasets of the chart
          var datasetColors = chart.data.datasets.map(function (e) {
            return e.backgroundColor;
          });

          datasetColors = datasetColors["flat"]();

          // Modify the color and hide state of each label
          labelsOriginal.forEach((label) => {
            // There are twice as many labels as there are datasets. This converts the label index into the corresponding dataset index
            label.datasetIndex = (label.index - (label.index % 2)) / 2;

            // The hidden state must match the dataset's hidden state
            label.hidden = !chart.isDatasetVisible(label.datasetIndex);

            // Change the color to match the dataset
            label.fillStyle = datasetColors[label.index];
          });

          return labelsOriginal;
        }
      },
      onClick: function (mouseEvent, legendItem, legend) {
        // toggle the visibility of the dataset from what it currently is
        legend.chart.getDatasetMeta(legendItem.datasetIndex).hidden = legend.chart.isDatasetVisible(
          legendItem.datasetIndex
        );
        legend.chart.update();
      }
    });
  }
  return options;
};

export const getBubbleData = (config: BubbleScatterChartConfig, data: BubbleChartPoint[]): ChartData => {
  const maxRadius = data.reduce((res, x) => Math.max(res, x.r), 0);
  return {
    datasets: [
      {
        backgroundColor: getColor(config.color, 0),
        borderColor: getColor(config.borderColor, 0),
        data: data.map((x) => ({
          ...x,
          r: Math.max((x.r / maxRadius) * 20, 1)
        })),
        hoverRadius: 0
      }
    ]
  };
};

export const getScatterOptions = (config: BubbleScatterChartConfig, data: Common.Point[]): ChartOptions => {
  const minYPoint = minBy(data, (x) => x.y);
  const maxYPoint = maxBy(data, (x) => x.y);

  const commonOptions = getCommonOptions(config);
  return {
    ...commonOptions,
    scales: {
      ...commonOptions.scales,
      x: {
        ...commonOptions.scales?.x,
        grid: {
          display: false
        },
        suggestedMin: 0,
        ticks: {
          stepSize: config.xAxis?.step
        }
      } as any, // TODO: TS issue
      y: {
        ...commonOptions.scales?.y,
        suggestedMin: minYPoint && minYPoint.y < 0 ? minYPoint.y : 0,
        suggestedMax: maxYPoint ? maxYPoint.y : 0,
        grid: {
          display: false
        }
      }
    }
  };
};
export const getScatterData = (config: BubbleScatterChartConfig, data: Common.Point[]): ChartData => ({
  datasets: [
    {
      backgroundColor: getColor(config.color, 0),
      data
    }
  ]
});
